/**
 * InputAdapter — abstrai keyboard + virtual joystick para movimento.
 * Usado pelo WorldScene para ler input do player.
 */

interface JoystickState {
  active: boolean;
  dx: number; // [-1..1]
  dy: number; // [-1..1]
}

class InputAdapterImpl {
  private keys: Record<string, boolean> = {};
  private joystick: JoystickState = { active: false, dx: 0, dy: 0 };
  private installed = false;

  install(): void {
    if (this.installed) return;
    this.installed = true;
    document.addEventListener('keydown', this.onKeyDown, true);
    document.addEventListener('keyup', this.onKeyUp, true);
    window.addEventListener('blur', this.onBlur);
  }

  uninstall(): void {
    if (!this.installed) return;
    this.installed = false;
    document.removeEventListener('keydown', this.onKeyDown, true);
    document.removeEventListener('keyup', this.onKeyUp, true);
    window.removeEventListener('blur', this.onBlur);
    this.keys = {};
  }

  private onKeyDown = (e: KeyboardEvent): void => {
    this.keys[e.code] = true;
    // Previne scroll do browser com setas + space
    if (
      e.code === 'ArrowUp' ||
      e.code === 'ArrowDown' ||
      e.code === 'ArrowLeft' ||
      e.code === 'ArrowRight' ||
      e.code === 'Space'
    ) {
      e.preventDefault();
    }
  };
  private onKeyUp = (e: KeyboardEvent): void => {
    this.keys[e.code] = false;
  };
  private onBlur = (): void => {
    this.keys = {};
    this.joystick = { active: false, dx: 0, dy: 0 };
  };

  setJoystick(active: boolean, dx: number, dy: number): void {
    this.joystick = {
      active,
      dx: Math.max(-1, Math.min(1, dx)),
      dy: Math.max(-1, Math.min(1, dy)),
    };
  }

  /** Vetor de movimento normalizado. Combina teclado + joystick. */
  getMovementVector(): { x: number; y: number } {
    let x = 0;
    let y = 0;
    if (this.keys['ArrowLeft'] || this.keys['KeyA']) x -= 1;
    if (this.keys['ArrowRight'] || this.keys['KeyD']) x += 1;
    if (this.keys['ArrowUp'] || this.keys['KeyW']) y -= 1;
    if (this.keys['ArrowDown'] || this.keys['KeyS']) y += 1;
    if (this.joystick.active) {
      x += this.joystick.dx;
      y += this.joystick.dy;
    }
    // Normaliza
    const mag = Math.hypot(x, y);
    if (mag > 1) {
      x /= mag;
      y /= mag;
    }
    return { x, y };
  }

  isExitPressed(): boolean {
    return this.keys['Escape'] === true;
  }

  isAttackPressed(): boolean {
    return this.keys['Space'] === true || this.keys['KeyJ'] === true || this.keys['KeyX'] === true;
  }

  isSkillPressed(): boolean {
    return this.keys['KeyQ'] === true || this.keys['KeyK'] === true;
  }

  isDodgePressed(): boolean {
    return this.keys['ShiftLeft'] === true || this.keys['ShiftRight'] === true || this.keys['KeyL'] === true;
  }
}

let _instance: InputAdapterImpl | null = null;

export function getInputAdapter(): InputAdapterImpl {
  if (!_instance) _instance = new InputAdapterImpl();
  return _instance;
}

export function resetInputAdapterForTests(): void {
  _instance?.uninstall();
  _instance = null;
}
